/**
 * The Ramp engine shared by Distribute and Clone to lines (W2-11 batch-tools; DESIGN.md §5.8, E§4.9 with the
 * errata fixed, E§4.10).
 *
 * For N items in order, n = 1..N:
 *   Y_n = curve((n - 1) / (N - 1))                (N = 1 -> Y = 0)
 *   X_n = lerp(from, to, Y_n) + U(-|jitter|, |jitter|)     (seeded; one draw per item, only when jitter != 0)
 *   Z_n = X_n * pattern[n mod len]                 (pattern 0-indexed, n 1-based; empty pattern = factor 1)
 * then the mode: Set v := Z, Add v + Z, Multiply v * Z, Max max(v, Z), Min min(v, Z), Mirror 2 * Z - v.
 *
 * Every step uses the expression language's own definitions (core/expr: `lerp`, `ease`, `bezier`, `rand`, `alt`),
 * in the same order, so the "equivalent expression" shown in the Distribute sheet produces bit-identical values
 * when it is applied to the same items in time order with the same seed. (The only intended difference: one item
 * gets Y = 0 exactly, where `ease(id, 0)` may return Phira's tiny endpoint value, e.g. InExpo(0) = 2^-10.)
 */

import { Ease } from '../ease/index';
import type { Ease as EaseValue } from '../ease/types';
import { Expr, formatEaseCall } from '../expr/index';
import type { AssignOp } from '../expr/types';
import type { Rng } from '../math/types';
import type { DistributeMode, RampSpec } from './types';
import { numText } from './common';

/** y of a ramp curve at u, exactly as the expression language's `ease(...)` / `bezier(...)` compute it. */
export function rampCurveY(curve: EaseValue, u: number): number {
  switch (curve.kind) {
    case 'hold':
      return 0;
    case 'tween':
      // Phira's linear curve is the identity; the expression language returns u itself too.
      return curve.id === 1 ? u : Ease.modelY(curve, u);
    case 'bezier':
      return Ease.bezierY(curve.p, Math.min(1, Math.max(0, u)));
  }
}

/** The expression language's `lerp(a, b, u)` (exactly `b` at u = 1). */
export function lerpExpr(a: number, b: number, u: number): number {
  return u === 1 ? b : a + (b - a) * u;
}

/** Pattern factor of item n (1-based): pattern[n mod len]; 1 for an empty pattern. */
export function patternFactor(pattern: readonly number[], n: number): number {
  const len = pattern.length;
  if (len === 0) return 1;
  return pattern[n % len] ?? 1;
}

/** Z_1..Z_count of a ramp (see the file header). `rng` is drawn once per item when the jitter is not 0. */
export function rampValues(r: RampSpec, count: number, rng: Rng): number[] {
  const out: number[] = [];
  const j = Math.abs(r.jitter);
  for (let i = 0; i < count; i++) {
    const u = count > 1 ? i / (count - 1) : 0;
    const y = count > 1 ? rampCurveY(r.curve, u) : 0;
    let x = lerpExpr(r.from, r.to, y);
    if (j !== 0) {
      const lo = -j;
      const hi = j;
      x = x + (lo + (hi - lo) * rng.next());
    }
    const z = x * patternFactor(r.pattern, i + 1);
    out.push(z === 0 ? 0 : z);
  }
  return out;
}

/** The mode applied to a current value v and the ramp value z. */
export function applyMode(mode: DistributeMode, v: number, z: number): number {
  switch (mode) {
    case 'set':
      return z;
    case 'add':
      return v + z;
    case 'multiply':
      return v * z;
    case 'max':
      return Math.max(v, z);
    case 'min':
      return Math.min(v, z);
    case 'mirror':
      return 2 * z - v;
  }
}

/** True when a mode reads the current value (Set does not). */
export function modeReadsValue(mode: DistributeMode): boolean {
  return mode !== 'set';
}

/** Round half away from zero (the expression language's `round`). */
export function roundHalfAway(x: number): number {
  return x < 0 ? -Math.round(-x) : Math.round(x);
}

/**
 * The right-hand side of a ramp in the expression language: `lerp(from, to, <curve of u>)`, plus
 * `rand(-j, j)` and `* alt(...)`. `alt` picks argument (i mod k) with i 0-based, so the pattern is listed rotated
 * by one (item n = i + 1 uses pattern[n mod len]).
 */
export function rampBody(r: RampSpec): string {
  let body = `lerp(${numText(r.from)}, ${numText(r.to)}, ${formatEaseCall(r.curve, 'u')})`;
  const j = Math.abs(r.jitter);
  if (j !== 0) body = `${body} + rand(${numText(-j)}, ${numText(j)})`;
  const len = r.pattern.length;
  if (len > 0 && !r.pattern.every((f) => f === 1)) {
    const rotated = r.pattern.map((_, i) => r.pattern[(i + 1) % len] ?? 1);
    body = `${j !== 0 ? `(${body})` : body} * alt(${rotated.map(numText).join(', ')})`;
  }
  return body;
}

/** The full equivalent program text of a ramp applied with a mode, e.g. "= lerp(-400, 400, ease(6, u)) * alt(-1, 1)". */
export function rampExpression(r: RampSpec, mode: DistributeMode, wrap?: (body: string) => string): string {
  const body0 = rampBody(r);
  const body = wrap === undefined ? body0 : wrap(body0);
  let assign: AssignOp = '=';
  let rhs = body;
  switch (mode) {
    case 'set':
      break;
    case 'add':
      assign = '+=';
      break;
    case 'multiply':
      assign = '*=';
      break;
    case 'max':
      rhs = `max(v, ${body})`;
      break;
    case 'min':
      rhs = `min(v, ${body})`;
      break;
    case 'mirror':
      rhs = `2 * (${body}) - v`;
      break;
  }
  return Expr.format(assign, rhs);
}
